import { Box, Slide, styled, Typography } from "@mui/material";
import { WifiOff } from "@procore/core-icons";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "app/redux-store-interactions";
import { selectIsOnline } from "slices/explorer";

// Visual spec from Figma "BIM - Offline Mode" / toast (node 198:32280):
// cream background, dark text, Inter Semi-Bold 14/20, drop-shadow,
// only the bottom corners rounded (it drops down from the top edge).
const Toast = styled(Box)({
    pointerEvents: "auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    padding: "4px 24px",
    borderBottomLeftRadius: 24,
    borderBottomRightRadius: 24,
    background: "#FDF6E2",
    color: "#313435",
    boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
    fontFamily: "Inter, sans-serif",
    fontSize: 14,
    fontWeight: 600,
    lineHeight: "20px",
    letterSpacing: 0.15,
    userSelect: "none",
    whiteSpace: "nowrap",
    "& svg": {
        color: "#C9A227",
        width: 24,
        height: 24,
    },
});

/**
 * Persistent visual indicator shown while the browser reports no network
 * connectivity. Lets the user understand why other controls are disabled.
 */
export function OfflineIndicator() {
    const { t } = useTranslation();
    const isOnline = useAppSelector(selectIsOnline);
    // Slide needs a stable container ref to anchor the transform off of.
    const containerRef = useRef<HTMLDivElement | null>(null);

    return (
        <Box ref={containerRef} sx={{ overflow: "hidden", pointerEvents: "none" }}>
            <Slide
                in={!isOnline}
                direction="down"
                container={containerRef.current}
                mountOnEnter
                unmountOnExit
                timeout={{ enter: 260, exit: 200 }}
            >
                <Box>
                    <Toast data-testid="offline-indicator" role="status" aria-live="polite">
                        <WifiOff size="md" />
                        <Typography
                            component="span"
                            sx={{
                                fontFamily: "inherit",
                                fontSize: "inherit",
                                fontWeight: "inherit",
                                lineHeight: "inherit",
                                letterSpacing: "inherit",
                                color: "inherit",
                            }}
                        >
                            {t("offlineIndicator")}
                        </Typography>
                    </Toast>
                </Box>
            </Slide>
        </Box>
    );
}
